import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ClipboardList, BedDouble, CalendarClock, Activity, UserPlus } from "lucide-react";

export default function DoctorDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const nav = useNavigate();

  const load = () => api.get("/doctor-dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <div>Loading...</div>;

  if (!d.doctor) {
    return <LinkDoctorPanel user={user} canAdmin={d.can_admin_link || ["admin", "super_admin"].includes(user?.role)} onLinked={load}/>;
  }

  const K = ({ icon: Ic, label, val, tone }) => (
    <div className="bg-white border p-4">
      <div className="flex justify-between">
        <div>
          <div className="label-tiny">{label}</div>
          <div className="font-display text-2xl font-semibold mt-2 data-mono">{val}</div>
        </div>
        <div className={`w-9 h-9 flex items-center justify-center bg-${tone}-50 text-${tone}-700`}><Ic className="w-5 h-5"/></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 p-4 flex items-center gap-4">
        <img src={d.doctor.photo || "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200"} className="w-16 h-16 object-cover rounded-sm" alt="" />
        <div>
          <h1 className="font-display text-xl font-semibold">Welcome, {d.doctor.name}</h1>
          <p className="text-sm text-slate-600">{d.doctor.department} · {d.doctor.qualification}</p>
        </div>
        <div className="ml-auto text-xs text-slate-600">
          Today · {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long" })}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <K icon={ClipboardList} label="Today's OPD" val={d.today.opd} tone="blue" />
        <K icon={BedDouble} label="Current IPD" val={d.today.ipd} tone="teal" />
        <K icon={CalendarClock} label="Appointments" val={d.today.appointments} tone="amber" />
        <K icon={Activity} label="Recent OPDs" val={d.recent_opds.length} tone="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border">
          <div className="p-3 border-b label-tiny">Today's OPD Queue ({d.opds_today.length})</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">OPD No</th><th className="label-tiny">Patient</th><th className="label-tiny">Time</th></tr></thead>
            <tbody>
              {d.opds_today.map((o) => (
                <tr key={o.id} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => nav(`/opd/${o.id}`)}>
                  <td className="p-2 font-mono text-xs">{o.opd_no}</td><td>{o.patient_name}</td><td className="text-xs">{fmtDT(o.created_at)}</td>
                </tr>
              ))}
              {d.opds_today.length === 0 && <tr><td colSpan="3" className="p-4 text-center text-sm text-slate-500">No OPDs today yet</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="bg-white border">
          <div className="p-3 border-b label-tiny">Current IPD Patients ({d.current_ipds.length})</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">IPD No</th><th className="label-tiny">Patient</th><th className="label-tiny">Bed</th></tr></thead>
            <tbody>
              {d.current_ipds.map((o) => (
                <tr key={o.id} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => nav(`/ipd/${o.id}`)}>
                  <td className="p-2 font-mono text-xs">{o.ipd_no}</td><td>{o.patient_name}</td><td className="text-xs font-mono">{o.bed_no || "-"}</td>
                </tr>
              ))}
              {d.current_ipds.length === 0 && <tr><td colSpan="3" className="p-4 text-center text-sm text-slate-500">No active admissions</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border">
        <div className="p-3 border-b label-tiny">Recent Consultations</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">OPD No</th><th className="label-tiny">Patient</th><th className="label-tiny">Diagnosis</th><th className="label-tiny">Date</th></tr></thead>
          <tbody>
            {d.recent_opds.map((o) => (
              <tr key={o.id} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => nav(`/opd/${o.id}`)}>
                <td className="p-2 font-mono text-xs">{o.opd_no}</td><td>{o.patient_name}</td><td className="text-xs">{o.diagnosis}</td><td className="text-xs">{fmtDT(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function LinkDoctorPanel({ user, canAdmin, onLinked }) {
  const [doctors, setDoctors] = useState([]);
  const [users, setUsers] = useState([]);
  const [targetUserId, setTargetUserId] = useState(user?.id || "");
  const [doctorId, setDoctorId] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newDoc, setNewDoc] = useState({ department: "General", qualification: "", opd_charge: 500, ipd_charge: 1000, phone: "" });

  useEffect(() => {
    if (!canAdmin) return;
    api.get("/doctors").then((r) => setDoctors(r.data)).catch(() => {});
    api.get("/users").then((r) => {
      const rows = (r.data || []).filter((u) => u.role === "doctor" && !u.doctor_id);
      setUsers(rows);
      // If caller is themselves a doctor without a link, default target = self
      if (user?.role === "doctor" && rows.some((u) => u.id === user.id)) {
        setTargetUserId(user.id);
      } else if (rows.length > 0) {
        setTargetUserId(rows[0].id);
      } else {
        setTargetUserId("");
      }
    }).catch(() => {});
  }, [canAdmin, user]);

  const doLink = async () => {
    setBusy(true);
    try {
      const payload = showCreate ? { doctor_id: "", ...newDoc } : { doctor_id: doctorId };
      const { data } = await api.post(`/users/${targetUserId}/link-doctor`, payload);
      if (data.ok) {
        toast.success(`Linked to ${data.doctor.name}`);
        onLinked && onLinked();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link failed");
    } finally { setBusy(false); }
  };

  if (!canAdmin) {
    return (
      <div className="bg-amber-50 border border-amber-200 p-6" data-testid="doctor-not-linked">
        <h2 className="font-display text-lg font-semibold">Doctor profile not linked</h2>
        <p className="text-sm text-slate-600 mt-2">Your login (<b>{user?.name}</b>) is not linked to a doctor record. Please ask an administrator to log in and visit this same page — they will see a <b>Link Doctor</b> panel to fix it in one click.</p>
      </div>
    );
  }

  const targetName = users.find((u) => u.id === targetUserId)?.name;

  return (
    <div className="bg-white border p-6 max-w-2xl" data-testid="link-doctor-panel">
      <h2 className="font-display text-lg font-semibold">Link doctor profile</h2>
      <p className="text-sm text-slate-500 mt-1">
        {targetName
          ? <>You are linking <b>{targetName}</b> to a doctor record. Choose an existing doctor OR create a new one.</>
          : <>All doctor accounts appear to be linked already. If you still see the &quot;profile not linked&quot; message elsewhere, refresh this page.</>}
      </p>

      <div className="mt-4 space-y-3">
        <div>
          <Label className="label-tiny">Unlinked doctor user to link</Label>
          {users.length === 0 ? (
            <div className="text-xs text-slate-500 bg-slate-50 border p-3">
              No unlinked doctor users found. All doctor accounts are already linked to a doctor record.
            </div>
          ) : (
            <select value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} className="w-full h-9 border px-2 text-sm" data-testid="link-target-user">
              {users.map((u) => <option key={u.id} value={u.id}>{u.name + " — " + u.email}</option>)}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs">
          <button type="button" onClick={() => setShowCreate(false)} data-testid="link-mode-existing"
            className={`px-3 py-1 border rounded-sm ${!showCreate ? "border-blue-700 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300"}`}>Link to existing doctor</button>
          <button type="button" onClick={() => setShowCreate(true)} data-testid="link-mode-create"
            className={`px-3 py-1 border rounded-sm ${showCreate ? "border-blue-700 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300"}`}>
            <UserPlus className="w-3 h-3 inline mr-1"/> Create new doctor from this user
          </button>
        </div>

        {!showCreate && (
          <div>
            <Label className="label-tiny">Existing doctor</Label>
            <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)} className="w-full h-9 border px-2 text-sm" data-testid="link-doctor-select">
              <option value="">-- Select doctor --</option>
              {doctors.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.department}</option>)}
            </select>
          </div>
        )}

        {showCreate && (
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="label-tiny">Department</Label>
              <Input value={newDoc.department} onChange={(e) => setNewDoc({ ...newDoc, department: e.target.value })} data-testid="link-new-dept"/>
            </div>
            <div><Label className="label-tiny">Qualification</Label>
              <Input value={newDoc.qualification} onChange={(e) => setNewDoc({ ...newDoc, qualification: e.target.value })} placeholder="MBBS, MD" data-testid="link-new-qual"/>
            </div>
            <div><Label className="label-tiny">OPD Charge (₹)</Label>
              <Input type="number" value={newDoc.opd_charge} onChange={(e) => setNewDoc({ ...newDoc, opd_charge: parseFloat(e.target.value) || 0 })} data-testid="link-new-opd"/>
            </div>
            <div><Label className="label-tiny">IPD Charge / day (₹)</Label>
              <Input type="number" value={newDoc.ipd_charge} onChange={(e) => setNewDoc({ ...newDoc, ipd_charge: parseFloat(e.target.value) || 0 })} data-testid="link-new-ipd"/>
            </div>
            <div className="col-span-2"><Label className="label-tiny">Phone</Label>
              <Input value={newDoc.phone} onChange={(e) => setNewDoc({ ...newDoc, phone: e.target.value })} data-testid="link-new-phone"/>
            </div>
          </div>
        )}

        <Button data-testid="link-submit" onClick={doLink}
          disabled={busy || !targetUserId || (!showCreate && !doctorId)}
          className="bg-blue-700 hover:bg-blue-800 rounded-sm">
          {showCreate ? "Create & Link" : "Link Doctor"}
        </Button>
      </div>
    </div>
  );
}
